import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { EstatActivacio, EstatDestinatari, TipusActivacio, TipusArea } from '@/api/types';
import { Icona, type NomIcona } from '@/components/Icona';
import { badgePerEstat } from '@/theme/theme';
import { colorAvatar, inicials } from './suport';
import './operacio.css';

/** Estat d'una activació com als mockups: oberta amb punt vermell, en curs amb rellotge. */
export function BadgeEstat({ estat }: { estat: EstatActivacio }) {
  const { t } = useTranslation('comu');
  const obert = estat === 'OBERTA' || estat === 'EN_CURS';
  return (
    <span className={`badge badge--${obert ? 'gray' : badgePerEstat(estat)}`} data-estat={estat}>
      {estat === 'OBERTA' ? <i className="dot dot--red pulse" /> : null}
      {estat === 'EN_CURS' ? <Icona nom="clock" /> : null}
      {t(`estatActivacio.${estat}`)}
    </span>
  );
}

const ICONA_DESTINATARI: Partial<Record<EstatDestinatari, NomIcona>> = {
  CONFIRMAT: 'checkmark-circle',
  FALLIT: 'xmark-circle',
  EXPIRAT: 'exclamationmark-circle',
  LLEGIT: 'eye',
};

export function BadgeEstatDestinatari({ estat }: { estat: EstatDestinatari }) {
  const { t } = useTranslation('comu');
  const clau = estat === 'CONFIRMAT' ? 'CONFIRMADA' : estat === 'EXPIRAT' ? 'EXPIRADA' : estat;
  const icona = ICONA_DESTINATARI[estat];
  return (
    <span className={`badge badge--${badgePerEstat(clau)}`} data-estat={estat}>
      {icona ? <Icona nom={icona} /> : null}
      {t(`estatDestinatari.${estat}`)}
    </span>
  );
}

/** Icona d'estat d'un destinatari (columna dreta del detall W-04). */
export function IconaEstatDestinatari({ estat }: { estat: EstatDestinatari }) {
  const { t } = useTranslation('comu');
  const [nom, color]: [NomIcona, string] =
    estat === 'CONFIRMAT'
      ? ['checkmark-circle', 'c-green']
      : estat === 'FALLIT' || estat === 'EXPIRAT'
        ? ['xmark-circle', 'c-red']
        : estat === 'EN_ESPERA' || estat === 'NO_NOTIFICAT'
          ? ['hourglass', 'c-gray']
          : ['clock', 'c-orange'];
  return (
    <Icona nom={nom} className={`list-row__chev ${color}`} titol={t(`estatDestinatari.${estat}`)} />
  );
}

export function BadgeTipus({ tipus, critica }: { tipus: TipusActivacio; critica?: boolean }) {
  const { t } = useTranslation('comu');
  if (critica) {
    return (
      <span className="badge badge--critic" data-critica="true">
        {t('tipusActivacio.CRITICA')}
      </span>
    );
  }
  return (
    <span className={`badge badge--${badgePerEstat(tipus)}`}>{t(`tipusActivacio.${tipus}`)}</span>
  );
}

export function BadgeArea({ tipus }: { tipus?: TipusArea | null }) {
  const { t } = useTranslation('comu');
  if (!tipus) return null;
  return (
    <span className={`badge badge--${badgePerEstat(tipus)}`} data-area={tipus}>
      {t(`tipusArea.${tipus}`)}
    </span>
  );
}

export function Avatar({
  nom,
  crisi,
  color,
  mida,
}: {
  nom?: string | null;
  crisi?: boolean;
  /** Classe de color (bg-blue…); per defecte es deriva del nom. */
  color?: string;
  mida?: 'm' | 'l';
}) {
  const fons = !nom ? 'bg-gray' : (color ?? (crisi ? 'bg-purple' : colorAvatar(nom)));
  return (
    <span className={`avatar ${fons}${mida ? ` avatar--${mida}` : ''}`}>
      {nom ? inicials(nom) : '—'}
    </span>
  );
}

/** Targeta d'indicador (mockups `.stat`): etiqueta amb icona, valor gran i línia de color. */
export function Kpi({
  etiqueta,
  valor,
  detall,
  to,
  icona,
}: {
  etiqueta: string;
  valor: ReactNode;
  detall?: ReactNode;
  to?: 'critic' | 'crisi' | 'ok' | 'up' | 'down';
  icona?: NomIcona;
}) {
  const classeDetall =
    to === 'critic' || to === 'down'
      ? ' down'
      : to === 'ok' || to === 'up'
        ? ' up'
        : to === 'crisi'
          ? ' crisi'
          : '';
  return (
    <div className="stat kpi">
      <div className="stat__label kpi__label">
        {icona ? <Icona nom={icona} /> : null}
        {etiqueta}
      </div>
      <div className="stat__value kpi__value">{valor}</div>
      {detall ? <div className={`stat__delta kpi__detail${classeDetall}`}>{detall}</div> : null}
    </div>
  );
}

export function Barra({ valor, total, color }: { valor: number; total: number; color?: string }) {
  const pct = total > 0 ? Math.round((valor / total) * 100) : 0;
  return (
    <span
      className="barra"
      role="progressbar"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span className="barra__fill" style={{ width: `${pct}%`, background: color }} />
    </span>
  );
}

export function Modal({
  titol,
  onTanca,
  children,
  peu,
  screen,
  icona,
  ample,
  capcalera,
}: {
  titol: string;
  onTanca: () => void;
  children: ReactNode;
  peu?: ReactNode;
  screen?: string;
  icona?: ReactNode;
  ample?: boolean;
  /** Contingut addicional a la capçalera (p. ex. passos de l'assistent). */
  capcalera?: ReactNode;
}) {
  const { t } = useTranslation('comu');
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onTanca();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onTanca]);
  return (
    <div className="modal-fons" onClick={onTanca}>
      <div
        className={`modal${ample ? ' modal--ample' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={titol}
        data-screen={screen}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="panel__head">
          {icona}
          <h3>{titol}</h3>
          <div className="toolbar__spacer" />
          {capcalera}
          <button
            type="button"
            className="modal__tanca"
            onClick={onTanca}
            aria-label={t('accions.tancar')}
            title={t('accions.tancar')}
          >
            <Icona nom="xmark" />
          </button>
        </div>
        <div className="panel__body form">{children}</div>
        {peu ? <div className="modal__peu">{peu}</div> : null}
      </div>
    </div>
  );
}

export function Camp({ etiqueta, children }: { etiqueta: string; children: ReactNode }) {
  return (
    <label className="camp">
      <span className="camp__label">{etiqueta}</span>
      {children}
    </label>
  );
}

/** Progrés de confirmacions «Confirmats 18/24» amb barra (llistes del Centre i d'Activacions). */
export function ProgresConfirmacions({
  confirmats,
  total,
  color,
  etiqueta,
}: {
  confirmats: number;
  total: number;
  color?: string;
  etiqueta?: string;
}) {
  const pct = total > 0 ? Math.round((confirmats / total) * 100) : 0;
  return (
    <span className="progres">
      {etiqueta ? (
        <span className="progres__cap">
          <span className="dim">{etiqueta}</span>
          <strong>
            {confirmats}/{total}
          </strong>
        </span>
      ) : null}
      <span className="progres__fila">
        <span
          className="progress progres__barra"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={etiqueta}
        >
          <i style={{ width: `${pct}%`, background: color }} />
        </span>
        {etiqueta ? null : (
          <strong className="progres__n">
            {confirmats}/{total}
          </strong>
        )}
      </span>
    </span>
  );
}

/** Botó de trucada (enllaç `tel:`); només es mostra si es coneix el telèfon. */
export function BotoTrucada({ nom, telefon }: { nom: string; telefon?: string | null }) {
  const { t } = useTranslation('operacio');
  if (!telefon) return null;
  const text = t('centre.truca', { nom });
  return (
    <a
      className="btn btn--s btn--icona"
      href={`tel:${telefon.replace(/\s+/g, '')}`}
      aria-label={text}
      title={`${text} · ${telefon}`}
    >
      <Icona nom="phone" />
    </a>
  );
}

/** Estat buit amb icona (mockups `.empty`). */
export function Buit({ icona = 'tray', children }: { icona?: NomIcona; children: ReactNode }) {
  return (
    <div className="empty">
      <Icona nom={icona} />
      {children}
    </div>
  );
}
